import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useUnreadCount from '../../hooks/useUnreadCount';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/button';
import ConfidentialWatermark from '../ConfidentialWatermark';
import { Shield, ArrowLeft, Sun, Moon, LogOut, CalendarRange, Store, MessageSquare, Megaphone } from 'lucide-react';
import { cn } from '../../lib/utils';

const CR_TABS = [
  { label: 'Event Calendar', path: '/employee/cr/events', icon: CalendarRange },
  { label: 'Retail Calendar', path: '/employee/cr/retail', icon: Store },
  { label: 'Messages', path: '/employee/cr/messages', icon: MessageSquare },
  { label: 'Announcements', path: '/employee/cr/announcements', icon: Megaphone },
];

// Full-width shell for CONTROL ROOM pages inside the employee portal.
export default function ControlRoomLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const msgUnread = useUnreadCount(`${process.env.REACT_APP_BACKEND_URL}/api/admin/staff-messages/unread-count`);
  const { theme, toggleTheme } = useTheme();

  if (user && !(user.role === 'employee' && user.admin_department === 'Control Room')) {
    return <Navigate to="/employee" replace />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <ConfidentialWatermark user={user} />
      <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/employee')} data-testid="cr-back-to-my-area" className="text-muted-foreground hover:text-foreground -ml-2 shrink-0">
              <ArrowLeft className="w-4 h-4 mr-1" /> My Area
            </Button>
            <Shield className="w-5 h-5 text-primary shrink-0" strokeWidth={1.5} />
            <span className="font-barlow tracking-wider text-primary uppercase text-sm sm:text-base truncate">
              San Security — <span className="text-orange-400">Control Room</span>
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="cr-theme-toggle" className="text-muted-foreground hover:text-foreground">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => { logout(); navigate('/login'); }} data-testid="cr-logout" className="text-muted-foreground hover:text-foreground">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-2 flex gap-1 overflow-x-auto pb-0.5">
          {CR_TABS.map((t) => {
            const active = location.pathname === t.path;
            return (
              <Link key={t.path} to={t.path} data-testid={`cr-tab-${t.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs uppercase tracking-wider whitespace-nowrap transition-colors border-b-2',
                  active ? 'bg-orange-500/15 text-orange-400 border-orange-400 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/50')}>
                <t.icon className={cn('w-3.5 h-3.5', !active && 'text-orange-500 dark:text-orange-400')} strokeWidth={1.75} />
                {t.label}
                {t.path.endsWith('/messages') && msgUnread > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="dept-messages-unread-badge">{msgUnread}</span>
                )}
              </Link>
            );
          })}
        </div>
      </header>
      <main className="flex-1 pt-28 pb-8 px-4">
        <div className="max-w-7xl mx-auto">{children}</div>
      </main>
    </div>
  );
}
